/**
 * Utility functions for handling boolean values
 */

/**
 * Normalizes various boolean-like values to boolean true/false.
 * Handles: yes/no, y/n, 1/0, true/false (case insensitive)
 * @param value - The value to normalize
 * @returns boolean true/false when recognized; otherwise returns the original value unchanged
 */
export const normalizeBooleanValue = (value: unknown): boolean | unknown => {
  if (value === null || value === undefined) {
    return value;
  }

  // Convert to string and trim whitespace
  const normalizedValue = String(value).trim().toLowerCase();

  // Handle empty values
  if (normalizedValue === '') {
    return value;
  }

  // Define boolean-like values
  const trueValues = ['yes', 'y', '1', 'true'];
  const falseValues = ['no', 'n', '0', 'false'];

  // Check if the value is a boolean-like value
  if (trueValues.includes(normalizedValue)) {
    return true;
  }

  if (falseValues.includes(normalizedValue)) {
    return false;
  }

  // Return original value if not a boolean-like value
  return value;
};

/**
 * Checks if a value is a boolean-like value that can be normalized
 * @param value - The value to check
 * @returns true if the value can be normalized to a boolean
 */
export const isBooleanLikeValue = (value: string | null | undefined): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  const normalizedValue = String(value).trim().toLowerCase();
  const booleanLikeValues = ['yes', 'y', 'no', 'n', '1', '0', 'true', 'false'];

  return booleanLikeValues.includes(normalizedValue);
};
